import * as React from 'react';

import * as chrono from 'chrono-node';
import type Moment from 'moment';
import moment from 'moment';

import type {SelectComponentValue, ComparisonRangeType, DateRange} from 'nutshell-core/types';
import {prettifyDateRange} from 'nutshell-core/utils';

import {Button} from '../button/button';
import {Checkbox} from '../checkbox/checkbox';
import {Select} from '../select/select';
import {TextField} from '../textfield/textfield';

import styles from './date-range-popover-form-section.module.css';

// @ts-expect-error
const comparisonValues = (prevPeriodText) => [
    {
        id: 'prevYear',
        value: 'Year-over-year',
    },
    {
        id: 'prevPeriod',
        value: `Previous period (${prevPeriodText})`,
    },
];

type Props = {
    hasComparison: boolean;
    useComparisonMode: boolean;
    onUseComparisonModeChange: (arg1: boolean) => any;
    comparisonType: ComparisonRangeType;
    comparisonDates: {
        prevPeriod: DateRange;
        prevYear: DateRange;
    };
    onComparisonChange: (arg1: ComparisonRangeType) => any;
    selectValue: string;
    onPeriodChange: (arg1: string) => any;
    // @ts-expect-error
    onStartOfRangeChange: (arg1: Moment) => any;
    // @ts-expect-error
    onEndOfRangeChange: (arg1: Moment) => any;
    onSubmit: () => any;
    onCancel: () => any;
    dateRangeOptions: Array<{
        id: string;
        value: string;
    }>;
    // @ts-expect-error
    selectedDates: [Moment, Moment];
    onStartOfRangeFocus: () => any;
    onEndOfRangeFocus: () => any;
    isCurrentlyDisplayingTypedDates: boolean;
};

type State = {
    startOfRangeText: string;
    endOfRangeText: string;
};

export class DateRangePopoverFormSection extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            startOfRangeText: props.selectedDates[0].format('M/D/YY'),
            endOfRangeText: props.selectedDates[1].format('M/D/YY'),
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (nextProps.selectValue !== this.props.selectValue) {
            this.setState({
                startOfRangeText: nextProps.selectedDates[0].format('M/D/YY'),
                endOfRangeText: nextProps.selectedDates[1].format('M/D/YY'),
            });
        }

        if (
            !nextProps.isCurrentlyDisplayingTypedDates &&
            !nextProps.isCurrentlyDisplayingTypedDates !==
                this.props.isCurrentlyDisplayingTypedDates
        ) {
            this.setState({
                startOfRangeText: nextProps.selectedDates[0].format('M/D/YY'),
                endOfRangeText: nextProps.selectedDates[1].format('M/D/YY'),
            });
        }

        // Check if the user changed the start date to be after the end date.
        // If so, adjust the end date to match
        if (nextProps.selectedDates[0].isAfter(nextProps.selectedDates[1])) {
            this.props.onEndOfRangeChange(nextProps.selectedDates[0]);
        }
        if (nextProps.selectedDates[1] !== this.props.selectedDates[1]) {
            this.setState({
                endOfRangeText: nextProps.selectedDates[1].format('M/D/YY'),
            });
        }
    }

    render() {
        const prevPeriodText = prettifyDateRange(
            this.props.comparisonDates.prevPeriod.startDate,
            this.props.comparisonDates.prevPeriod.endDate
        );
        const compareSelectOptions = comparisonValues(prevPeriodText);

        return (
            <div className={styles.container}>
                <div className={styles.header}>Date range</div>
                <div className={styles['form-container']}>
                    <div className={styles.form}>
                        <Select
                            valueKey='id'
                            labelKey='value'
                            styleOptions={{filled: true}}
                            selectValues={this.props.dateRangeOptions}
                            onChange={this.handlePeriodChange}
                            value={this.props.dateRangeOptions.find(
                                (val) => val.id === this.props.selectValue
                            )}
                        />
                        <div className={styles['date-range-inputs']}>
                            <TextField
                                onChange={this.handleStartOfRangeTextChange}
                                onFocus={this.props.onStartOfRangeFocus}
                                value={this.state.startOfRangeText}
                                selectOnFocus={true}
                            />
                            <TextField
                                onChange={this.handleEndOfRangeTextChange}
                                onFocus={this.props.onEndOfRangeFocus}
                                value={this.state.endOfRangeText}
                                selectOnFocus={true}
                            />
                        </div>
                        {this.props.hasComparison ? (
                            <div className={styles['compare-container']}>
                                <Checkbox
                                    label='Compare to:'
                                    checked={this.props.useComparisonMode}
                                    onChange={this.handleToggleComparisonMode}
                                />
                                <Select
                                    disabled={!this.props.useComparisonMode}
                                    valueKey='id'
                                    labelKey='value'
                                    styleOptions={{filled: true}}
                                    selectValues={compareSelectOptions}
                                    onChange={this.handleComparisonChange}
                                    value={compareSelectOptions.find(
                                        (val) => val.id === this.props.comparisonType
                                    )}
                                />
                            </div>
                        ) : undefined}
                    </div>
                    <div className={styles['form-submit']}>
                        <Button variant='secondary' size='big' onClick={this.props.onCancel}>
                            Cancel
                        </Button>
                        <Button variant='save' size='big' onClick={this.props.onSubmit}>
                            Apply
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    handlePeriodChange = (period: SelectComponentValue) => {
        this.props.onPeriodChange(period.id);
    };

    handleComparisonChange = (comparisonType: {id: ComparisonRangeType}) => {
        this.props.onComparisonChange(comparisonType.id);
    };

    handleToggleComparisonMode = (isChecked: boolean) => {
        this.props.onUseComparisonModeChange(isChecked);
    };

    handleStartOfRangeTextChange = (val: string) => {
        // Assume our parsedDate is invalid
        let parsedDateIsValid = false;

        // Actually parse the thing with chrono
        const parsedDate = chrono.parseDate(val);

        // Even if parsedDate is null, we can still create a valid javascript
        // date object and therefore moment object here, which is nice.
        // @ts-expect-error
        const momentParsedDate = moment(new Date(parsedDate));

        // If chrono returned non-null (a valid date), then mark it valid.
        // If it's after the end date, we will adjust the end date, rather
        // than consider the start invalid
        if (parsedDate) {
            parsedDateIsValid = true;
        }

        // Always update the startOfRangeText value, but optionally flip
        // the isValid state
        this.setState({
            startOfRangeText: val,
        });

        // If we're a valid date, we need to let our parent know that the
        // date was successfully changed
        if (parsedDateIsValid) {
            this.props.onStartOfRangeChange(momentParsedDate);
        }
    };

    handleEndOfRangeTextChange = (val: string) => {
        // Assume our parsedDate is invalid
        let parsedDateIsValid = false;

        // Actually parse the thing with chrono
        const parsedDate = chrono.parseDate(val);

        // Even if parsedDate is null, we can still create a valid javascript
        // date object and therefore moment object here, which is nice.
        // @ts-expect-error
        const momentParsedDate = moment(new Date(parsedDate));

        // If chrono returned non-null (a valid date), check to make usre
        // it's after (or the same) as the startDate, and before (or the same) as
        // today, then mark it valid.
        if (parsedDate) {
            const startDate = this.props.selectedDates[0];
            const dayBeforeStartDate = startDate.clone().subtract(1, 'days');

            if (momentParsedDate.isAfter(dayBeforeStartDate, 'day')) {
                parsedDateIsValid = true;
            }
        }

        // Always update the startOfRangeText value, but optionally flip
        // the isValid state
        this.setState({
            endOfRangeText: val,
        });

        // If we're a valid date, we need to let our parent know that the
        // date was successfully changed
        if (parsedDateIsValid) {
            this.props.onEndOfRangeChange(momentParsedDate);
        }
    };
}
